import { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { cn } from '../lib/cn';
import { useLockBodyScroll } from '../lib/hooks';
import { Button, IconButton } from './Button';
import styles from './Dialog.module.css';

const EXIT_MS = 180;
const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), iframe, [tabindex]:not([tabindex="-1"])';

// Only the top-most open dialog reacts to Escape.
const stack = [];

/** Keeps the dialog mounted while its exit animation plays. */
function usePresence(open) {
  const [mounted, setMounted] = useState(open);
  const [closing, setClosing] = useState(false);

  useEffect(() => {
    if (open) {
      setMounted(true);
      setClosing(false);
      return undefined;
    }
    if (!mounted) return undefined;
    setClosing(true);
    const id = setTimeout(() => {
      setMounted(false);
      setClosing(false);
    }, EXIT_MS);
    return () => clearTimeout(id);
  }, [open, mounted]);

  return { mounted, closing };
}

function useDialogBehaviour({ open, panelRef, onClose, initialFocusRef }) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!open) return undefined;
    const token = {};
    stack.push(token);
    const previous = document.activeElement;
    const panel = panelRef.current;

    const focusFirst = () => {
      const target =
        initialFocusRef?.current || panel?.querySelector('[data-autofocus]') || panel;
      target?.focus?.({ preventScroll: true });
    };
    const raf = requestAnimationFrame(focusFirst);

    const onKeyDown = (event) => {
      if (stack[stack.length - 1] !== token) return;
      if (event.key === 'Escape') {
        event.stopPropagation();
        onCloseRef.current?.();
        return;
      }
      if (event.key !== 'Tab' || !panel) return;
      const items = Array.from(panel.querySelectorAll(FOCUSABLE)).filter(
        (el) => el.getClientRects().length > 0
      );
      if (!items.length) {
        event.preventDefault();
        return;
      }
      const first = items[0];
      const last = items[items.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === panel)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    return () => {
      cancelAnimationFrame(raf);
      document.removeEventListener('keydown', onKeyDown);
      const index = stack.indexOf(token);
      if (index >= 0) stack.splice(index, 1);
      if (previous && typeof previous.focus === 'function' && document.contains(previous)) {
        previous.focus({ preventScroll: true });
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
}

function DialogShell({
  open,
  onClose,
  kind,
  className,
  overlayClassName,
  closeOnOverlay = true,
  labelledBy,
  describedBy,
  ariaLabel,
  initialFocusRef,
  children,
}) {
  const panelRef = useRef(null);
  const { mounted, closing } = usePresence(open);
  useLockBodyScroll(mounted);
  useDialogBehaviour({ open: mounted && !closing, panelRef, onClose, initialFocusRef });

  if (!mounted || typeof document === 'undefined') return null;

  return createPortal(
    <div
      className={cn(styles.root, kind === 'modal' ? styles.modalRoot : [styles.drawerRoot, styles[kind]], closing && styles.closing, overlayClassName)}
      onMouseDown={(event) => {
        if (closeOnOverlay && event.target === event.currentTarget) onClose?.();
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={labelledBy}
        aria-describedby={describedBy}
        aria-label={labelledBy ? undefined : ariaLabel}
        tabIndex={-1}
        className={cn(styles.panel, className)}
      >
        {children}
      </div>
    </div>,
    document.body
  );
}

/**
 * Centered dialog. size: sm | md | lg | xl. On phones it becomes a bottom sheet
 * (or full screen with `fullscreenOnMobile`).
 */
export function Modal({
  open,
  onClose,
  title,
  description,
  size = 'md',
  footer,
  hideClose = false,
  fullscreenOnMobile = false,
  closeOnOverlay = true,
  initialFocusRef,
  className,
  bodyClassName,
  children,
}) {
  const id = useId();
  const titleId = title ? `${id}-title` : undefined;
  const descId = description ? `${id}-desc` : undefined;

  return (
    <DialogShell
      open={open}
      onClose={onClose}
      kind="modal"
      labelledBy={titleId}
      describedBy={descId}
      ariaLabel={typeof title === 'string' ? title : 'Dialog'}
      closeOnOverlay={closeOnOverlay}
      initialFocusRef={initialFocusRef}
      className={cn(styles[`size-${size}`], fullscreenOnMobile && styles.fullscreenMobile, className)}
    >
      {(title || !hideClose) && (
        <header className={cn(styles.header, !title && styles.headerBare)}>
          <div className={styles.headings}>
            {title && (
              <h2 id={titleId} className={styles.title}>
                {title}
              </h2>
            )}
            {description && (
              <p id={descId} className={styles.description}>
                {description}
              </p>
            )}
          </div>
          {!hideClose && (
            <IconButton label="Close" onClick={onClose} className={styles.close}>
              <X />
            </IconButton>
          )}
        </header>
      )}
      <div className={cn(styles.body, bodyClassName)}>{children}</div>
      {footer && <footer className={styles.footer}>{footer}</footer>}
    </DialogShell>
  );
}

/** Side panel (right by default). Full screen on phones. */
export function Drawer({
  open,
  onClose,
  title,
  subtitle,
  side = 'right',
  width = 480,
  header,
  footer,
  hideClose = false,
  closeOnOverlay = true,
  initialFocusRef,
  className,
  bodyClassName,
  children,
}) {
  const id = useId();
  const titleId = title ? `${id}-title` : undefined;

  return (
    <DialogShell
      open={open}
      onClose={onClose}
      kind={`drawer-${side}`}
      labelledBy={titleId}
      ariaLabel={typeof title === 'string' ? title : 'Panel'}
      closeOnOverlay={closeOnOverlay}
      initialFocusRef={initialFocusRef}
      className={className}
    >
      <div className={styles.drawerInner} style={{ '--drawer-w': `${width}px` }}>
        {header ??
          ((title || !hideClose) && (
            <header className={styles.header}>
              <div className={styles.headings}>
                {title && (
                  <h2 id={titleId} className={styles.title}>
                    {title}
                  </h2>
                )}
                {subtitle && <p className={styles.description}>{subtitle}</p>}
              </div>
              {!hideClose && (
                <IconButton label="Close" onClick={onClose} className={styles.close}>
                  <X />
                </IconButton>
              )}
            </header>
          ))}
        <div className={cn(styles.body, styles.drawerBody, bodyClassName)}>{children}</div>
        {footer && <footer className={cn(styles.footer, styles.drawerFooter)}>{footer}</footer>}
      </div>
    </DialogShell>
  );
}

/** Yes/no confirmation. `tone="danger"` styles the confirm button for destructive actions. */
export function ConfirmDialog({
  open,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'primary',
  loading = false,
  onConfirm,
  onCancel,
  children,
}) {
  return (
    <Modal
      open={open}
      onClose={loading ? undefined : onCancel}
      title={title}
      description={description}
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
    </Modal>
  );
}
